import { CheckCircle2, Circle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { LauraStageValidationItem } from "./lauraStageTypes";
import { useTranslation } from "@/app/i18n/LocaleContext";

type Props = {
    items: LauraStageValidationItem[];
    onToggle: (item: LauraStageValidationItem, checked: boolean) => void;
    title?: string;
};

export function ValidationCheckpointPanel({ items, onToggle, title }: Props) {
    const { t } = useTranslation();
    if (items.length === 0) return null;

    const checkedCount = items.filter((i) => i.checked).length;
    const allChecked = checkedCount === items.length;

    return (
        <Card className="border-dashed">
            <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2">
                    <CardTitle className="text-sm">
                        {title ?? t("laura", "validationTitle")}
                    </CardTitle>
                    <Badge variant={allChecked ? "default" : "secondary"}>
                        {checkedCount}/{items.length}
                    </Badge>
                </div>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
                {!allChecked && (
                    <Alert>
                        <AlertTitle className="text-xs">
                            {t("laura", "validationPending")}
                        </AlertTitle>
                        <AlertDescription className="text-xs">
                            {t("laura", "validationHint")}
                        </AlertDescription>
                    </Alert>
                )}
                <ul className="flex flex-col gap-2">
                    {items.map((item) => (
                        <li key={item.id}>
                            <label
                                className={cn(
                                    "flex cursor-pointer items-start gap-2 rounded-md border p-2 text-sm transition-colors hover:bg-muted/50",
                                    item.checked && "border-primary/30 bg-primary/5",
                                )}
                            >
                                <input
                                    type="checkbox"
                                    className="sr-only"
                                    checked={item.checked}
                                    onChange={(e) =>
                                        onToggle(item, e.target.checked)
                                    }
                                />
                                {item.checked ? (
                                    <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />
                                ) : (
                                    <Circle className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                                )}
                                <span>{item.text}</span>
                            </label>
                        </li>
                    ))}
                </ul>
            </CardContent>
        </Card>
    );
}
